import { Button, Card, Checkbox, Col, Divider, Flex, Form, Grid, Input, Row, Segmented } from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';

// components
import BackButton from 'components/backButton/backButton';
import ComponentHeader from 'components/componentHeader/componentHeader';
import FileInput from 'components/fileInput/fileInput';
import FormElement from 'components/formElement/formElement';
import PageTitle from 'components/title/title';
import DatePickerElement from 'components/datePicker/datePicker';
import MapComponent from '../../../../../components/map/map';

// services

// styles
import googleMeetLogo from 'assets/icons/google_meet.svg';

// models
import { Assembly, AssemblyLocation, AssemblyType, Decision, DecisionFormItem } from '../models/assemblies';
import FormNumberElement from 'components/formNumberElement/formNumberElement';
import VerificationModal from '../../verificationModal';
import { VerificationPayload } from '../../commonModels';
import NotificationService from 'core/services/notification';
import { onGetAssemblyById, onGetStaff, onUpdateAssembly } from '../services/assembliesServices';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import FormListFilesElement from 'components/documentsListForm/documentsListForm';
import AssemblyPointsElement from '../components/points';
import SelectElement from 'components/selectElement/selectElement';

interface ActionAssembliesEditPageProps {}

const ActionAssembliesEditPage: FunctionComponent<ActionAssembliesEditPageProps> = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();

	const [assemblyForm] = Form.useForm();

	const [staff, setStaff] = useState<{ label: string; value: number; meta: any }[]>([]);

	const [assembly, setAssembly] = useState<Assembly>();
	const [assemblyType, setAssemblyType] = useState<AssemblyType>('Ordinary');
	const [assemblyLocation, setAssemblyLocation] = useState<AssemblyLocation[]>(['Online']);

	const [isLoading, setIsLoading] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [verificationModalOpen, setVerificationModalOpen] = useState(false);

	const assemblyTypeOptions = [
		{
			value: 'Ordinary',
			label: (
				<p className="fw-medium mb-0" style={{ fontSize: 15 }}>
					{t('ACTIONS.ORDINARY')}
				</p>
			),
		},
		{
			value: 'Extraordinary',
			label: (
				<p className="fw-medium mb-0" style={{ fontSize: 15 }}>
					{t('ACTIONS.EXTRAORDINARY')}
				</p>
			),
		},
	];

	useEffect(() => {
		getData();
	}, []);

	const getData = async () => {
		try {
			setIsLoading(true);

			await Promise.all([getAssemblyDetails(), getStaff()]);

			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			navigate('/dashboard/actions/assemblies');
		}
	};

	const getAssemblyDetails = async () => {
		try {
			const response = await onGetAssemblyById(id!);

			setAssembly(response);
			setAssemblyType(response?.type);
			setAssemblyLocation(response.location_type == 'Both' ? ['Online', 'Physical'] : [response.location_type]);

			assemblyForm.setFieldsValue({ ...response, date: dayjs(response.date) });
		} catch (error) {
			navigate('/dashboard/actions/assemblies');
			NotificationService.error(t('COMMON.ERROR'), t('ACTIONS.ERROR_FETCHING_ASSEMBLY'));
		}
	};

	const getStaff = async () => {
		try {
			const response = await onGetStaff();

			setStaff(response.map((staff) => ({ label: staff?.name, value: staff?.id, meta: { type: staff?.type, signature: staff?.signature } })));
		} catch (error) {}
	};

	const onChangeAssemblyLocation = (location: AssemblyLocation, checked: boolean) => {
		if (checked) {
			setAssemblyLocation([...assemblyLocation, location]);
			assemblyForm.setFieldsValue({ location_type: [...assemblyLocation, location] });
		} else {
			const tempLocation = [...assemblyLocation];
			tempLocation.splice(tempLocation.indexOf(location), 1);
			setAssemblyLocation(tempLocation);
			assemblyForm.setFieldsValue({ location_type: tempLocation });
		}
	};

	const onValidateForm = async () => {
		setVerificationModalOpen(true);
	};

	const onSubmit = async ({ key, method }: VerificationPayload) => {
		try {
			setIsSubmitting(true);

			const payload = new FormData();

			payload.append('_method', 'PUT');
			payload.append('verified_using', method);
			payload.append(method, key);
			payload.append('type', assemblyType);
			payload.append('assembly_quorum_percentage', assemblyForm.getFieldValue('assembly_quorum_percentage'));
			payload.append('location_type', assemblyLocation.length == 2 ? 'Both' : assemblyLocation[0]);
			payload.append('datetime', dayjs(assemblyForm.getFieldValue('date')).format('YYYY-MM-DD hh:mm a'));

			payload.append('general_secretary_id', assemblyForm.getFieldValue('general_secretary_id'));
			payload.append('general_secretary_type', assemblyForm.getFieldValue('general_secretary_type'));
			payload.append('vote_collector_id', assemblyForm.getFieldValue('vote_collector_id'));
			payload.append('vote_collector_type', assemblyForm.getFieldValue('vote_collector_type'));

			if (assemblyLocation.includes('Physical')) {
				payload.append('physical_lat', assemblyForm.getFieldValue('physical_lat'));
				payload.append('physical_lng', assemblyForm.getFieldValue('physical_lng'));
				payload.append('physical_address', assemblyForm.getFieldValue('physical_address'));
				payload.append('physical_address_description', assemblyForm.getFieldValue('physical_address_description'));
			}

			assemblyForm.getFieldValue('agendas')?.forEach((fileObj: { file: File; title: string; id: string }, index: number) => {
				if (fileObj.id) payload.append(`files[${index}][id]`, fileObj.id);
				payload.append(`files[${index}][title]`, fileObj.title);
				if (fileObj.file) payload.append(`files[${index}][file]`, fileObj.file);
			});

			assemblyForm.getFieldValue('decisions')?.forEach((point: DecisionFormItem, index: number) => {
				payload.append(`decisions[${index}][ar]`, point.translated_title.ar ?? '');
				payload.append(`decisions[${index}][en]`, point.translated_title.en ?? '');
				payload.append(`decisions[${index}][quorum_percentage]`, point.quorum_percentage.toString());
				payload.append(`decisions[${index}][type]`, point.type);
				payload.append(`decisions[${index}][abstention_type]`, point.abstention_type);

				if (point.abstention_type === 'specific_shareholders')
					point.abstaining_shareholder_ids?.forEach((shareholder_id: string, shIndex: number) => {
						payload.append(`decisions[${index}][abstaining_shareholder_ids][${shIndex}]`, shareholder_id);
					});
			});

			await onUpdateAssembly(assembly?.id!, payload);

			setIsSubmitting(false);

			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.ASSEMBLY_UPDATED'));
			navigate('/dashboard/actions/assemblies');
		} catch (error: any) {
			setIsSubmitting(false);

			const errorMessage = error?.response?.data?.data?.errors || [error?.response?.data?.message];
			const messages = Object.values(errorMessage || {}).flat() as string[];

			const errors = (
				<ul>
					{messages.map((msg, index) => (
						<li key={index}>{msg}</li>
					))}
				</ul>
			);

			NotificationService.error(t('COMMON.ERROR'), messages.length ? errors : t('COMMON.SUBMIT_ERROR'));
		}
	};

	return (
		<>
			<PageTitle name={t('TITLE.ACTIONS')} />

			<BackButton />

			{isLoading ? (
				<LoadingComponent />
			) : (
				<Card>
					<Form
						form={assemblyForm}
						onFinish={onValidateForm}
						initialValues={{
							files: [{}],
							vote_collector_id: assembly?.vote_collector?.id,
							vote_collector_type: assembly?.vote_collector?.type,
							general_secretary_id: assembly?.general_secretary?.id,
							general_secretary_type: assembly?.general_secretary?.type,
						}}>
						<ComponentHeader title={t('ACTIONS.ASSEMBLIES')} subtitle={t('ACTIONS.FILL_TO_REVIEW')} />

						<div>
							<Segmented
								block
								className="custom white"
								size="middle"
								options={assemblyTypeOptions}
								onChange={(value) => {
									setAssemblyType(value as AssemblyType);
									assemblyForm.validateFields(['assembly_quorum_percentage']);
								}}
								value={assemblyType}
							/>
						</div>

						<Row gutter={[16, 16]} className="mt-5">
							<Col span={24} md={12}>
								<FormNumberElement
									rules={[
										{ required: true, message: t('COMMON.FIELD_REQUIRED') },
										{
											type: 'number',
											min: assemblyType == 'Ordinary' ? 25 : 50,
											message: t('ACTIONS.QUORUM_MUST_BE_ATLEAST', { num: assemblyType == 'Ordinary' ? 25 : 50 }),
										},
									]}
									name="assembly_quorum_percentage"
									label={t('ACTIONS.ATTENDANCE_QUORUM')}
									min={0}
									max={100}
									precision={2}
									addonAfter={<span className="px-3 fw-semibold fw-se">%</span>}
								/>
							</Col>

							<Col span={24} md={12}>
								<DatePickerElement
									name="date"
									rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}
									label={t('ACTIONS.CHOOSE_ASSEMBLY_DATE')}
									minDate={dayjs().add(1, 'day')}
									onChange={() => {}}
									showTime={{ format: 'hh:mm a' }}
								/>
							</Col>
						</Row>

						<Row gutter={[16, 16]} className="mt-5">
							<Col span={24} md={12}>
								<SelectElement
									options={staff}
									label={t('ACTIONS.GENERAL_SECRETARY_NAME')}
									name="general_secretary_id"
									rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}
									onChange={(_, meta) => assemblyForm.setFieldValue('general_secretary_type', meta?.type)}
								/>
								<Form.Item name="general_secretary_type" hidden noStyle />
							</Col>

							<Col span={24} md={12}>
								<SelectElement
									options={staff}
									label={t('ACTIONS.VOTE_COLLECTOR_NAME')}
									name="vote_collector_id"
									rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}
									onChange={(_, meta) => assemblyForm.setFieldValue('vote_collector_type', meta?.type)}
								/>
								<Form.Item name="vote_collector_type" hidden noStyle />
							</Col>
						</Row>

						<Divider />

						<div>
							<h6 className="mt-3 mb-2">{t('ACTIONS.LOCATION')}</h6>
							<div className="mb-3">
								<Checkbox
									checked={assemblyLocation.includes('Online')}
									className="compoundCheckbox"
									onChange={(e) => onChangeAssemblyLocation('Online', e.target.checked)}>
									<Flex align="center" gap={5}>
										<p className="fw-medium mb-0">{t('ACTIONS.ONLINE')} - </p> <img src={googleMeetLogo} style={{ height: 25 }} />
									</Flex>
									<span className="text-light">{t('ACTIONS.ONLINE_MEETING')}</span>
								</Checkbox>
							</div>
							<div>
								<Checkbox
									checked={assemblyLocation.includes('Physical')}
									className="compoundCheckbox"
									onChange={(e) => onChangeAssemblyLocation('Physical', e.target.checked)}>
									<div>
										<p className="fw-medium mb-0">{t('ACTIONS.PHYSICAL')} </p>
										<span className="text-light">{t('ACTIONS.PHYSICAL_MEETING')}</span>
									</div>
								</Checkbox>
							</div>
							<Form.Item
								name="location_type"
								initialValue={assemblyLocation}
								rules={[{ required: true, message: t('ACTIONS.SELECT_AT_LEAST_ONE_LOCATION') }]}>
								<Input hidden />
							</Form.Item>
						</div>

						{assemblyLocation.includes('Physical') && (
							<div className="my-4">
								<MapComponent
									lat={+assemblyForm.getFieldValue('physical_lat')}
									lng={+assemblyForm.getFieldValue('physical_lng')}
									onSelect={(lat, lng, address) => {
										assemblyForm.setFieldsValue({ physical_lat: lat, physical_lng: lng, physical_address: address });
									}}
								/>

								<div className="mt-3">
									<Form.Item name="physical_lat" hidden rules={[{ required: assemblyLocation.includes('Physical') }]}>
										<Input hidden />
									</Form.Item>
									<Form.Item name="physical_lng" hidden rules={[{ required: assemblyLocation.includes('Physical') }]}>
										<Input hidden />
									</Form.Item>

									<FormElement
										fullWidth
										name="physical_address"
										label={t('ACTIONS.ADDRESS')}
										type="text"
										rules={[{ required: assemblyLocation.includes('Physical'), message: t('COMMON.FIELD_REQUIRED') }]}
									/>

									<FormElement
										fullWidth
										name="physical_address_description"
										label={t('ACTIONS.ADDRESS_DESC')}
										type="text"
										rules={[{ required: assemblyLocation.includes('Physical'), message: t('COMMON.FIELD_REQUIRED') }]}
									/>
								</div>
							</div>
						)}

						<Divider />

						<ComponentHeader title={t('ACTIONS.AGENDA_FOR_MEETING')} subtitle={t('ACTIONS.ADD_ALL_FILES_TO_ASSEMBLY')} />

						<FormListFilesElement form={assemblyForm} fieldName="agendas" editMode />

						<Divider />

						<AssemblyPointsElement form={assemblyForm} decisions={assembly?.decisions} />

						<Col span={24}>
							<Flex justify="flex-end" align="center" gap={20} className="mt-5">
								<Button type="primary" htmlType="submit">
									{t('COMMON.UPDATE')}
								</Button>
							</Flex>
						</Col>
					</Form>
				</Card>
			)}

			<VerificationModal
				open={verificationModalOpen}
				note={dayjs(assemblyForm.getFieldValue('date')).isBefore(dayjs().add(21, 'day')) ? t('ACTIONS.ASSEMBLY_LESS_THAN_21_DAYS_WARNING') : null}
				noteType="red"
				isLoading={isSubmitting}
				onCancel={() => setVerificationModalOpen(false)}
				onVerificationSuccess={onSubmit}
				module="financialStatement"
			/>
		</>
	);
};

export default ActionAssembliesEditPage;
